import type { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { Card } from '@/components/ui/card'
import { Activity, CalendarClock, Clock } from 'lucide-react'
import { agentChatHref } from '@/frontend/layouts/worker-first/agentAccess'
import type { WorkerStats, CronData } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { remainingPercent } from '@/frontend/utils/tokenUsage'

interface Props {
  workerId: string
  stats: WorkerStats
  crons?: CronData | null
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 1,
  }).format(value)
}

// Semânticas de uso: ok/warn/err — sem paleta crua por faixa.
function barColor(percent: number | null): string {
  if (percent === null) return 'bg-muted-foreground/20'
  if (percent >= 100) return 'bg-destructive'
  if (percent >= 80) return 'bg-warning'
  return 'bg-success'
}

// Stat do protótipo (número 19/650 tabular + label 11.5 baixa), agora como
// LINK: cada card leva à página correspondente (Uso, chat, Rotinas).
function StatCard({ icon, label, value, secondary, href, testId }: {
  icon: ReactNode
  label: string
  value: string
  secondary?: ReactNode
  href: string
  testId: string
}) {
  return (
    <Link
      href={href}
      data-testid={testId}
      className="block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card className="h-full min-h-[120px] gap-0 rounded-xl px-[15px] py-[13px] transition-colors duration-150 hover:bg-accent/50">
        <span className="flex min-h-7 items-center text-[19px] font-semibold leading-snug tracking-[-0.01em] text-foreground tabular-nums">
          {value}
        </span>
        <span className="flex min-h-5 items-center gap-1.5 text-[11.5px] text-faint-foreground">
          {icon}
          {label}
        </span>
        <div className="mt-auto flex min-h-5 w-full items-end pt-2">
          {secondary}
        </div>
      </Card>
    </Link>
  )
}

function UsageBar({ usedPercent }: { usedPercent: number | null }) {
  const availablePercent = remainingPercent(usedPercent)
  if (availablePercent === null) return <span className="text-xs text-muted-foreground">Ilimitado</span>

  return (
    <div className="w-full pb-0.5">
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor(usedPercent)}`}
          style={{ width: `${availablePercent}%` }}
        />
      </div>
    </div>
  )
}

// Strip de stats da mini-home do agente (Iteração 2, Tema 3): SÓ os três que
// o founder nomeou — Disponível hoje · Conversas ativas (últimos 15 min) · Rotinas —
// e cada card é clicável para a página correspondente (Uso, chat, Rotinas).
// "Conversas totais" saiu. Rotinas só têm contagem quando os crons já
// chegaram (a landing não busca a Fleet, SZ-1598).
export default function WorkerStatsGrid({ workerId, stats, crons }: Props) {
  const base = `/dashboard/digital_workers/${workerId}`
  const enabledCrons = crons?.status?.enabledJobs ?? null

  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="worker-stats-grid">
      <StatCard
        icon={<CalendarClock className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />}
        label="Disponível hoje"
        value={stats.daily_usage_percent !== null
          ? `${formatPercent(remainingPercent(stats.daily_usage_percent) ?? 0)}%`
          : 'Ilimitado'}
        secondary={<UsageBar usedPercent={stats.daily_usage_percent} />}
        href={`${base}/uso`}
        testId="worker-stat-uso"
      />
      <StatCard
        icon={<Activity className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />}
        label="Conversas ativas"
        value={stats.active_sessions.toLocaleString('pt-BR')}
        secondary={
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              {stats.active_sessions > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-60" />}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${stats.active_sessions > 0 ? 'bg-success' : 'bg-muted-foreground/30'}`} />
            </span>
            <span className="text-xs text-muted-foreground">nos últimos 15 min</span>
          </div>
        }
        href={agentChatHref(workerId)}
        testId="worker-stat-conversas"
      />
      <StatCard
        icon={<Clock className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />}
        label="Rotinas ativas"
        value={enabledCrons !== null ? enabledCrons.toLocaleString('pt-BR') : '—'}
        secondary={enabledCrons !== null
          ? <span className="text-xs text-muted-foreground">{enabledCrons === 1 ? 'rotina agendada' : 'rotinas agendadas'}</span>
          : <span className="text-xs text-muted-foreground">Abrir rotinas</span>
        }
        href={`${base}/rotinas`}
        testId="worker-stat-rotinas"
      />
    </div>
  )
}
